export enum InterviewEventType {
    LinkSent = 'link_sent',
    ValidLinkClicked = 'valid_link_clicked',
    ProceededToSetup = 'proceeded_to_setup',
    RoomGenerated = 'room_generated',
    CameraDevicePermissionDenied = 'camera_device_permission_denied',
    CameraDevicesIdentified = 'camera_devices_identified',
    CameraCheckCompleted = 'camera_check_completed',
    MicDevicePermissionDenied = 'mic_device_permission_denied',
    MicDevicesIdentified = 'mic_devices_identified',
    MicCheckCompleted = 'mic_check_completed',
    MicCheckBypass = 'mic_check_bypass',
    SpeakerDevicesIdentified = 'speaker_devices_identified',
    SpeakerAudioPlayed = 'speaker_audio_played',
    SpeakerCheckConfirmed = 'speaker_check_confirmed',
    LivenessInitiated = 'liveness_initiated',
    LivenessCompleted = 'liveness_completed',
    ScreenShareInit = 'screen_share_init',
    ScreenShareDevicePermissionDenied = 'screen_share_device_permission_denied',
    ScreenShareCompleted = 'screen_share_completed',
    ProceededToInterview = 'proceeded_to_interview',
    NetworkCheckCompleted = 'network_check_completed',
    ConnectionStatsSample = 'connection_stats_sample',
    SpeakerStateChange = 'speaker_state_change',
    SpeakerStateOscillation = 'speaker_state_oscillation',
    FullscreenEntered = 'fullscreen_entered',
    FullscreenExited = 'fullscreen_exited',
}

export interface InterviewEventMetadata {
    count?: number
    roomName?: string
    rttMs?: number
    downloadMbps?: number
    uploadMbps?: number
    level?: 'good' | 'warn'
    screenShareRequired?: boolean
    micNoiseFloor?: number
    /** Ambient room noise (dBFS) measured on the raw capture during mic check. */
    ambientNoiseDbfs?: number
    micSpeechPeak?: number
    micSnr?: number
    micQualityLevel?: 'good' | 'warn' | 'fail'
    micQualityProfile?: 'default' | 'processed'
    automaticProcessing?: {
        staticGain: number
        dynamicGain: number | null
        maxDynamicGain: number | null
        rnnoiseApplied: boolean
        maxPeak: number
        clippedSamples: number
        peakSamples: number
        clippingRatio: number
    }
    sttConfidence?: number
    tier?: 'good' | 'fair' | 'poor' | 'very-poor'
    packetLossPct?: number
    jitterMs?: number
    // Speaker state tracking
    from?: 'idle' | 'listening' | 'speaking' | 'processing'
    to?: 'idle' | 'listening' | 'speaking' | 'processing'
    source?: string
    changes?: number
    windowMs?: number
    history?: { state: string; timestamp: number; source: string }[]
}

export interface InterviewEvent {
    type: InterviewEventType | string
    timestamp: string
    metadata?: InterviewEventMetadata
}

export function getInterviewEventLabel(event: InterviewEvent | { type: string; metadata?: InterviewEventMetadata }): string {
    const { type, metadata } = event
    switch (type) {
        case InterviewEventType.LinkSent:
            return 'Interview link sent'
        case InterviewEventType.ValidLinkClicked:
            return 'Valid link clicked'
        case InterviewEventType.ProceededToSetup:
            return 'Proceeded to setup'
        case InterviewEventType.RoomGenerated:
            return metadata?.roomName ? `Room generated: ${metadata.roomName}` : 'Room generated'
        case InterviewEventType.CameraDevicePermissionDenied:
            return 'Camera device permission denied'
        case InterviewEventType.CameraDevicesIdentified:
            return metadata?.count != null ? `Camera devices identified: ${metadata.count}` : 'Camera devices identified'
        case InterviewEventType.CameraCheckCompleted:
            return 'Camera check completed'
        case InterviewEventType.MicDevicePermissionDenied:
            return 'Mic device permission denied'
        case InterviewEventType.MicDevicesIdentified:
            return metadata?.count != null ? `Mic devices identified: ${metadata.count}` : 'Mic devices identified'
        case InterviewEventType.MicCheckCompleted:
            return metadata?.micQualityLevel === 'warn' ? 'Mic check completed (warning)' : 'Mic check completed'
        case InterviewEventType.MicCheckBypass:
            return 'Mic check bypass'
        case InterviewEventType.SpeakerDevicesIdentified:
            return metadata?.count != null ? `Speaker devices identified: ${metadata.count}` : 'Speaker devices identified'
        case InterviewEventType.SpeakerAudioPlayed:
            return 'Speaker audio played'
        case InterviewEventType.SpeakerCheckConfirmed:
            return 'Speaker check confirmed'
        case InterviewEventType.LivenessInitiated:
            return 'Liveness initiated'
        case InterviewEventType.LivenessCompleted:
            return 'Liveness completed'
        case InterviewEventType.ScreenShareInit:
            return 'Screen share init'
        case InterviewEventType.ScreenShareDevicePermissionDenied:
            return 'Screen share device permission denied'
        case InterviewEventType.ScreenShareCompleted:
            return 'Screen share completed'
        case InterviewEventType.ProceededToInterview:
            return 'Proceeded to interview'
        case InterviewEventType.NetworkCheckCompleted:
            return metadata?.level === 'warn' ? 'Network check completed (warning)' : 'Network check completed'
        case InterviewEventType.ConnectionStatsSample:
            return metadata?.rttMs != null ? `Connection stats sample (${metadata.rttMs} ms)` : 'Connection stats sample'
        case InterviewEventType.FullscreenEntered:
            return 'Fullscreen entered'
        case InterviewEventType.FullscreenExited:
            return 'Fullscreen exited'
        default:
            return String(type)
    }
}
